import React, {useEffect, useState} from 'react'
import {Col, Row, Table} from 'react-bootstrap'
import {numberFormat} from '../../../utils/Functions'
import AccountOrderSummary from '../AccountOrderSummary'
import {KTCard, KTCardBody} from '../../../../_metronic/helpers'
import {IIncomeDocument} from '../IncomeDocumentDetailPage'
import {
  DEBT_ADDITION_REASON,
  DEBT_REDUCTION_REASON,
  DEBT_REDUCTION_SCOPE,
  IncomeDocumentTypes,
  PRODUCT_RECEIVE_OPTION,
  PRODUCT_RETURN_OPTION,
} from '../../../constants/Common'
import {round} from 'lodash'
import AccountOrderDepositSummary from '../AccountOrderDepositSummary'
import {Link, useLocation, useSearchParams} from 'react-router-dom'
import {getRevenuePrefixPath} from '../../../utils/Helper'
import StateLabel from '../../grid/StateLabel'
import ExpenseAccountOrderSummary from '../ExpenseAccountOrderSummary'
import moment from 'moment'
import api from '../../../utils/Api'
import {
  DEBT_ADDITION_REASONS,
  DEBT_REDUCTION_REASONS,
  PRODUCT_RECEIVE_OPTIONS,
  PRODUCT_RETURN_OPTIONS,
} from '../../../utils/common'

interface ITabDocumentDetail {
  document: IIncomeDocument
}

export interface IProductPriceInfo {
  total: number
  subTotal: number
  depositTotal?: number
  beforeDepositTotal?: number
  beforeCorrectPrice?: number
  correctPrice?: number
  discountTotal: number
  whTaxTotal: number
  vatTotal: number
  vatItemPriceTotal: number
  zeroVatItemPriceTotal: number
  noVatItemPriceTotal: number
  paidAmount?: number
  paidWhTaxPrice?: number
  remainingAmount?: number
  paymentDeductionsAmount?: number
  paymentDeductionsVatPrice?: number
  paymentDeductionsWhTaxPrice?: number
  paymentCombinedAmount?: number
  paymentFeeAmount?: number
  /** Fixed document amount due; unlike remainingAmount it never changes after payment. */
  amountDue?: number
  comBinedData?: {
    totalPaymentDepositBillAmount: number
    totalCreditNoteBillAmount: number
    totalPaymentDepositVatAmount: number
    totalCreditNoteVatAmount: number
    totalPaymentDepositWhTaxPriceAmount: number
    totalCreditNoteWhTaxPriceAmount: number
    totalBillAmount: number
    totalVatAmount: number
    totalWhTaxPriceAmount: number
    totalDecreaseWhTaxPriceAmount: number
    totalDecreaseBillAmount: number
    totalDecreaseVatAmount: number
  }
}

export interface IProductDepositPriceInfo {
  total: number
  subTotal: number
  depositTotal?: number
  beforeDepositTotal?: number
  beforeCorrectPrice?: number
  correctPrice?: number
  discountTotal: number
  whTaxPrice: number
  vatTotal: number
  vatItemPriceTotal: number
  zeroVatItemPriceTotal: number
  noVatItemPriceTotal: number
}

const TabDocumentDetail: React.FC<ITabDocumentDetail> = ({document}) => {
  const [searchParams] = useSearchParams()
  const docType = searchParams.get('type')

  const isExpense = docType
    ? [
        IncomeDocumentTypes.EXPENSE_PURCHASE_INVENTORY_RECORD,
        IncomeDocumentTypes.EXPENSE_EXPENSE_RECORD,
        IncomeDocumentTypes.EXPENSE_PURCHASE_CREDIT_NOTE,
        IncomeDocumentTypes.EXPENSE_PURCHASE_DEBIT_NOTE,
        IncomeDocumentTypes.EXPENSE_COMBINED_PAYMENT_NOTE,
      ].includes(docType as IncomeDocumentTypes)
    : false

  const isCreditDebit = [
    IncomeDocumentTypes.EXPENSE_PURCHASE_CREDIT_NOTE,
    IncomeDocumentTypes.EXPENSE_PURCHASE_DEBIT_NOTE,
  ].includes(document?.type)
  const isExpenseOnly = [
    IncomeDocumentTypes.EXPENSE_PURCHASE_INVENTORY_RECORD,
    IncomeDocumentTypes.EXPENSE_EXPENSE_RECORD,
  ].includes(document?.type)

  const createDefaultProductPriceInfo = (): IProductPriceInfo => ({
    total: 0,
    subTotal: 0,
    depositTotal: 0,
    beforeDepositTotal: 0,
    discountTotal: 0,
    whTaxTotal: 0,
    vatTotal: 0,
    vatItemPriceTotal: 0,
    zeroVatItemPriceTotal: 0,
    noVatItemPriceTotal: 0,
  })

  const defaultProductDepositPriceInfo: IProductDepositPriceInfo = {
    total: 0,
    subTotal: 0,
    depositTotal: 0,
    beforeDepositTotal: 0,
    discountTotal: 0,
    whTaxPrice: 0,
    vatTotal: 0,
    vatItemPriceTotal: 0,
    zeroVatItemPriceTotal: 0,
    noVatItemPriceTotal: 0,
  }

  const currentDepositDeductionDocuments = document?.incomeDocumentDepositDeductions
  const relateDepositDeductionDocuments = document?.linkingDocument?.incomeDocumentDepositDeductions

  const isCombined = [IncomeDocumentTypes.EXPENSE_COMBINED_PAYMENT_NOTE].includes(document?.type)

  let depositDocuments = currentDepositDeductionDocuments?.map((doc: any) => doc.deposit)
  if (
    document.type !== 'deposit' &&
    document.type !== 'expense-deposit-payment' &&
    relateDepositDeductionDocuments?.length
  ) {
    depositDocuments = relateDepositDeductionDocuments?.map((doc: any) => doc.deposit)
  }

  const isReceiptBilling =
    document.type === 'receipt' && document.linkingDocument?.billingDocuments?.length
  const shouldRenderIssueTaxInvoice = [
    'invoce',
    'expense-purchase-inventory-record',
    'expense-expense-record',
  ].includes(document.type)
  const location = useLocation()
  const [productPriceInfo, setProductPriceInfo] = useState(createDefaultProductPriceInfo)
  const [depositPriceInfo, setDepositPriceInfo] = useState(defaultProductDepositPriceInfo)
  const isInExpensePage = location.pathname.includes('expense')
  const isInExpenseRecordPage = document.type === 'expense-expense-record'
  const isAP =
    document?.expenseCode?.startsWith('AP') || document?.linkingDocument?.code.startsWith('AP')
  const isInExpenseRequestPurchaseOrder =
    document.type === IncomeDocumentTypes.EXPENSE_REQUEST_PURCHASE_ORDER
  const isInExpenseDepositPayment = document.type === IncomeDocumentTypes.EXPENSE_DEPOSIT_PAYMENT

  const incomeDocumentDepositDeductionAppliers =
    document?.incomeDocumentDepositDeductionAppliers || []

  const mappedDepositDeductionAppliers = incomeDocumentDepositDeductionAppliers.flatMap((item) => {
    const payments = item?.incomeDocument?.incomeDocumentPayments || []

    return payments.filter((payment: any) => !payment?.cancelledAt && !payment?.cancelled_at).map((payment: any) => ({
      id: payment.id,
      date: payment.date,
      code: item?.incomeDocument?.code,
      bank: payment.bank,
      accountNo: payment.accountNo,
      accountName: payment.accountName,
      amount: Number(item?.amount || 0) - Number(item?.whTaxPrice || 0),
      note: payment.note,
      incomeDocumentId: payment.incomeDocumentId,
      createdAt: payment.createdAt,
      updatedAt: payment.updatedAt,
      whTax: payment.whTax,
      whTaxPrice: Number(item.whTaxPrice),
      chequeNumber: payment.chequeNumber,
      chequeBranchNumber: payment.chequeBranchNumber,
      chequeBank: payment.chequeBank,
      chequeAccountNumber: payment.chequeAccountNumber,
      chequeDate: payment.chequeDate,
      receiptId: payment.receiptId,
      pndType: payment.pndType,
      taxCondition: payment.taxCondition,
      gatewayId: payment.gatewayId,
      wthType: payment.whTaxPrice,
      withholdingTaxItems: payment.withholdingTaxItems || [],
      receipt: payment.receipt || null,
    }))
  })
  const incomeDocumentPayments = [
    ...(document?.incomeDocumentPayments || []).filter(
      (payment: any) => !payment?.cancelledAt && !payment?.cancelled_at
    ),
    ...([
      IncomeDocumentTypes.EXPENSE_PURCHASE_CREDIT_NOTE,
      IncomeDocumentTypes.EXPENSE_PURCHASE_DEBIT_NOTE,
    ].includes(document.type)
      ? mappedDepositDeductionAppliers
      : []),
  ]

  const calculateSummaryPriceInfo = () => {
    // console.log({document})
    // เริ่มจาก object ใหม่เสมอ เพื่อไม่ให้ effect ที่คำนวณซ้ำสะสมยอดเดิม
    let sumInfo = createDefaultProductPriceInfo()

    let previousCorrectPrice = 0
    // BL (and a receipt linked to BL) derives its amounts from billingDocuments.
    // Its own item rows are display copies, so including them here and then
    // including the source IV rows below doubles the document total on screen.
    const isBillingBackedDocument = document.type === IncomeDocumentTypes.BILLING || isReceiptBilling
    const displayedItems = isBillingBackedDocument ? [] : document?.incomeDocumentItems || []
    displayedItems.map((item: any) => {
      const subTotal = item.beforeVatPrice
      const vatTotal = item.vatPrice

      sumInfo = {
        ...sumInfo,
        total: (sumInfo.total += subTotal),
        subTotal: (sumInfo.subTotal += subTotal - item.whTaxPrice),
        discountTotal: (sumInfo.discountTotal += item.discountPerUnit * item.quantity),
        whTaxTotal: document?.whTaxPrice || 0,
        zeroVatItemPriceTotal: (sumInfo.zeroVatItemPriceTotal += item.vat === 0 ? subTotal : 0),
        noVatItemPriceTotal: (sumInfo.noVatItemPriceTotal += item.vat === 99 ? subTotal : 0),
        vatTotal: (sumInfo.vatTotal += vatTotal),
        vatItemPriceTotal: (sumInfo.vatItemPriceTotal += item.vat === 7 ? subTotal : 0),
      }
    })

    if (!!document.incomeDocumentDepositDeductions.length) {
      sumInfo = {
        ...sumInfo,
        whTaxTotal: document.whTaxPrice,
      }
    }

    let totalWhTax = 0
    if (
      incomeDocumentPayments &&
      incomeDocumentPayments.length > 0 &&
      incomeDocumentPayments?.[0]?.extraWithholdingTaxItems &&
      incomeDocumentPayments?.[0]?.extraWithholdingTaxItems.length > 0
    ) {
      for (let i = 0; i < incomeDocumentPayments[0].extraWithholdingTaxItems.length; i++) {
        totalWhTax = totalWhTax + incomeDocumentPayments[0].extraWithholdingTaxItems[i].whTaxPrice
      }
    }
    let totalItemsWhTax = 0
    if (document?.incomeDocumentItems && document?.incomeDocumentItems.length > 0) {
      for (let i = 0; i < document?.incomeDocumentItems.length; i++) {
        totalItemsWhTax = totalItemsWhTax + document?.incomeDocumentItems[i].whTaxPrice
      }
    }

    const recomputedTotal =
      sumInfo.vatTotal +
      sumInfo.vatItemPriceTotal +
      sumInfo.noVatItemPriceTotal +
      sumInfo.zeroVatItemPriceTotal

    // ยึดยอดที่ stored (document.totalAmount) เป็นหลัก ให้ตรงกับหน้า list และบัญชีรายวัน
    // กัน rounding เพี้ยน 0.01 จากการ recompute รวม per-line VAT ใหม่โดยไม่ปัด
    const hasStoredTotal = document.totalAmount !== null && document.totalAmount !== undefined
    // BL/RV ที่อ้าง BL จะเติมยอดจาก billingDocuments ด้านล่างอยู่แล้ว จึงต้องไม่เอา
    // totalAmount ของเอกสารมาเป็นยอดตั้งต้น มิฉะนั้นยอดเดียวกันจะถูกบวกซ้ำอีกครั้ง.
    const total = isBillingBackedDocument
      ? recomputedTotal
      : isCombined || hasStoredTotal
      ? document.totalAmount
      : recomputedTotal

    const storedNetAmount = Number(document.netAmount)
    const invoiceAmountDue =
      document.type !== IncomeDocumentTypes.INVOICE
        ? undefined
        : Number.isFinite(storedNetAmount)
        ? storedNetAmount
        : total - sumInfo.discountTotal

    sumInfo = {
      ...sumInfo,
      total: total,
      subTotal: total - sumInfo.whTaxTotal,
      amountDue: invoiceAmountDue,
    }

    if (document?.incomeDocumentDepositDeductions?.length) {
      let depositSumInfo: IProductDepositPriceInfo = {
        total: 0,
        subTotal: 0,
        depositTotal: 0,
        beforeDepositTotal: 0,
        discountTotal: 0,
        whTaxPrice: 0,
        vatTotal: 0,
        vatItemPriceTotal: 0,
        zeroVatItemPriceTotal: 0,
        noVatItemPriceTotal: 0,
      }

      // console.log(
      //   'document.incomeDocumentDepositDeductions ==>>',
      //   document.incomeDocumentDepositDeductions
      // )
      document.incomeDocumentDepositDeductions.map((v: any) => {
        const depositDoc = v.deposit
        const creditDocuments = depositDoc.childrenDocuments.filter(
          (item: any) => item.type === 'credit'
        )
        const creditTotalAmount = creditDocuments.reduce((a: any, b: any) => a + b.totalAmount, 0)
        const creditVatTotalAmount = creditDocuments.reduce((a: any, b: any) => a + b.vatPrice, 0)

        depositDoc.incomeDocumentItems.map((vi: any) => {
          const netPricePerUnit = vi.pricePerUnit - vi.discountPerUnit
          let subTotal = v?.amount ? Number(v?.amount) : netPricePerUnit * vi.quantity
          let vatTotal = vi.vat === 7 ? subTotal * (vi.vat / 100) : 0
          // console.log({subTotal})
          if (depositDoc.vatType === 'included') {
            vatTotal = vi.vat === 7 ? round(subTotal - (subTotal * 100) / (100 + vi.vat), 2) : 0
          }

          if (v?.vatPrice) {
            subTotal += Number(v?.vatPrice)
          }

          // console.log({depositDoc})
          // console.log({subTotal})
          // console.log({vatTotal})

          depositSumInfo = {
            ...depositSumInfo,
            zeroVatItemPriceTotal: (depositSumInfo.zeroVatItemPriceTotal +=
              vi.vat === 0 ? subTotal : 0),
            noVatItemPriceTotal: (depositSumInfo.noVatItemPriceTotal +=
              vi.vat === 99 ? subTotal : 0),
            vatItemPriceTotal: (depositSumInfo.vatItemPriceTotal +=
              vi.vat === 7 ? subTotal - vatTotal : 0),
            vatTotal: (depositSumInfo.vatTotal += vatTotal),
          }
        })

        if (creditDocuments.length) {
          creditDocuments?.map((doc: any) => {
            doc?.incomeDocumentItems?.map((item: any) => {
              const netPricePerUnit = item.pricePerUnit - item.discountPerUnit
              const subTotal = netPricePerUnit * item.quantity
              let vatTotal = item.vat === 7 ? subTotal * (item.vat / 100) : 0

              if (depositDoc.vatType === 'included') {
                vatTotal =
                  item.vat === 7 ? round(subTotal - (subTotal * 100) / (100 + item.vat), 2) : 0
              }

              depositSumInfo = {
                ...depositSumInfo,
                zeroVatItemPriceTotal: (depositSumInfo.zeroVatItemPriceTotal -=
                  item.vat === 0 ? subTotal : 0),
                noVatItemPriceTotal: (depositSumInfo.noVatItemPriceTotal -=
                  item.vat === 99 ? subTotal : 0),
                vatItemPriceTotal: (depositSumInfo.vatItemPriceTotal -=
                  item.vat === 7 ? subTotal - vatTotal : 0),
                vatTotal: (depositSumInfo.vatTotal -= vatTotal),
              }
            })
          })
        }

        depositSumInfo = {
          ...depositSumInfo,
          vatTotal: round(depositSumInfo.vatTotal, 2),
          total: (depositSumInfo.total +=
            (v?.amount ? v?.amount : depositDoc.totalAmount) +
            (v?.vatPrice ? Number(v?.vatPrice) : 0) -
            creditTotalAmount),
          whTaxPrice: (depositSumInfo.whTaxPrice += depositDoc.whTaxPrice),
        }
      })

      // console.log(50050505050)
      // console.log({document})
      // console.log({depositSumInfo})
      // console.log(50050505050)

      setDepositPriceInfo(depositSumInfo)

      const depositAmountValue = depositSumInfo.total

      // EX-004: ยอดที่เก็บไว้ (document.totalAmount) เป็นยอดหลังหักมัดจำอยู่แล้ว เพราะฟอร์มตอน
      // สร้างเอกสารหักมัดจำออกก่อนบันทึก จึงต้องบวกมัดจำกลับเพื่อให้ได้ "มูลค่าก่อนหักเงินมัดจำ"
      // ไม่งั้นบรรทัดหลังหักมัดจำและกล่องจำนวนเงินทั้งสิ้นจะหักมัดจำซ้ำอีกรอบ
      const beforeDepositTotal = hasStoredTotal ? total + depositAmountValue : recomputedTotal

      // console.log({sumInfo})
      // console.log({depositAmountValue})

      sumInfo = {
        ...sumInfo,
        depositTotal: depositAmountValue,
        beforeDepositTotal: beforeDepositTotal,
        zeroVatItemPriceTotal: sumInfo.zeroVatItemPriceTotal - depositSumInfo.zeroVatItemPriceTotal,
        noVatItemPriceTotal: sumInfo.noVatItemPriceTotal - depositSumInfo.noVatItemPriceTotal,
        vatItemPriceTotal: sumInfo.vatItemPriceTotal - depositSumInfo.vatItemPriceTotal,
        vatTotal: sumInfo.vatTotal - depositSumInfo.vatTotal,
      }

      const dTotal =
        sumInfo.vatTotal +
        sumInfo.vatItemPriceTotal +
        sumInfo.noVatItemPriceTotal +
        sumInfo.zeroVatItemPriceTotal
      sumInfo = {
        ...sumInfo,
        total: dTotal,
        subTotal: dTotal - sumInfo.whTaxTotal,
      }
    }

    if (document.type === 'receipt') {
      let depositDeductionSumInfo: IProductDepositPriceInfo = {
        total: 0,
        subTotal: 0,
        depositTotal: 0,
        beforeDepositTotal: 0,
        discountTotal: 0,
        whTaxPrice: 0,
        vatTotal: 0,
        vatItemPriceTotal: 0,
        zeroVatItemPriceTotal: 0,
        noVatItemPriceTotal: 0,
      }

      relateDepositDeductionDocuments?.map((v: any) => {
        const depositDoc = v.deposit
        const creditDocuments = depositDoc.childrenDocuments.filter(
          (item: any) => item.type === 'credit'
        )
        const creditTotalAmount = creditDocuments.reduce((a: any, b: any) => a + b.totalAmount, 0)
        const creditVatTotalAmount = creditDocuments.reduce((a: any, b: any) => a + b.vatPrice, 0)

        depositDoc.incomeDocumentItems.map((vi: any) => {
          const netPricePerUnit = vi.pricePerUnit - vi.discountPerUnit
          const subTotal = netPricePerUnit * vi.quantity
          let vatTotal = vi.vat === 7 ? subTotal * (vi.vat / 100) : 0

          if (depositDoc.vatType === 'included') {
            vatTotal = vi.vat === 7 ? round(subTotal - (subTotal * 100) / (100 + vi.vat), 2) : 0
          }

          depositDeductionSumInfo = {
            ...depositDeductionSumInfo,
            zeroVatItemPriceTotal: (depositDeductionSumInfo.zeroVatItemPriceTotal +=
              vi.vat === 0 ? subTotal : 0),
            noVatItemPriceTotal: (depositDeductionSumInfo.noVatItemPriceTotal +=
              vi.vat === 99 ? subTotal : 0),
            vatItemPriceTotal: (depositDeductionSumInfo.vatItemPriceTotal +=
              vi.vat === 7
                ? depositDoc.vatType === 'included'
                  ? subTotal - vatTotal
                  : subTotal
                : 0),
            vatTotal: (depositDeductionSumInfo.vatTotal += vatTotal),
          }
        })

        if (creditDocuments.length) {
          creditDocuments?.map((doc: any) => {
            doc?.incomeDocumentItems?.map((item: any) => {
              const netPricePerUnit = item.pricePerUnit - item.discountPerUnit
              const subTotal = netPricePerUnit * item.quantity
              let vatTotal = item.vat === 7 ? subTotal * (item.vat / 100) : 0

              if (depositDoc.vatType === 'included') {
                vatTotal =
                  item.vat === 7 ? round(subTotal - (subTotal * 100) / (100 + item.vat), 2) : 0
              }

              depositDeductionSumInfo = {
                ...depositDeductionSumInfo,
                zeroVatItemPriceTotal: (depositDeductionSumInfo.zeroVatItemPriceTotal -=
                  item.vat === 0 ? subTotal : 0),
                noVatItemPriceTotal: (depositDeductionSumInfo.noVatItemPriceTotal -=
                  item.vat === 99 ? subTotal : 0),
                vatItemPriceTotal: (depositDeductionSumInfo.vatItemPriceTotal -=
                  item.vat === 7
                    ? depositDoc.vatType === 'included'
                      ? subTotal - vatTotal
                      : subTotal
                    : 0),
                vatTotal: (depositDeductionSumInfo.vatTotal -= vatTotal),
              }
            })
          })
        }

        depositDeductionSumInfo = {
          ...depositDeductionSumInfo,
          vatTotal: round(depositDeductionSumInfo.vatTotal, 2),
          total: (depositDeductionSumInfo.total += depositDoc.totalAmount - creditTotalAmount),
          whTaxPrice: (depositDeductionSumInfo.whTaxPrice += depositDoc.whTaxPrice),
        }
      })

      if (relateDepositDeductionDocuments?.length) {
        const beforeDepositTotal =
          document.vatType === 'excluded' ? total - sumInfo.vatTotal : total
        const depositAmountValue =
          document.vatType === 'excluded'
            ? depositDeductionSumInfo.noVatItemPriceTotal +
              depositDeductionSumInfo.zeroVatItemPriceTotal +
              depositDeductionSumInfo.vatItemPriceTotal
            : depositDeductionSumInfo.total

        sumInfo = {
          ...sumInfo,
          depositTotal: depositAmountValue,
          beforeDepositTotal: beforeDepositTotal,
        }

        setDepositPriceInfo(depositDeductionSumInfo)
      }

      sumInfo = {
        ...sumInfo,
        zeroVatItemPriceTotal:
          sumInfo.zeroVatItemPriceTotal - depositDeductionSumInfo.zeroVatItemPriceTotal,
        noVatItemPriceTotal:
          sumInfo.noVatItemPriceTotal - depositDeductionSumInfo.noVatItemPriceTotal,
        vatItemPriceTotal: sumInfo.vatItemPriceTotal - depositDeductionSumInfo.vatItemPriceTotal,
        vatTotal: sumInfo.vatTotal - depositDeductionSumInfo.vatTotal,
        total: sumInfo.total - depositDeductionSumInfo.total,
        subTotal:
          sumInfo.subTotal - depositDeductionSumInfo.total + depositDeductionSumInfo.whTaxPrice,
        whTaxTotal: sumInfo.whTaxTotal - depositDeductionSumInfo.whTaxPrice,
      }
    }

    if (document.type === 'billing' || isReceiptBilling) {
      let billingDocuments = document.billingDocuments
      if (isReceiptBilling) {
        billingDocuments = document.linkingDocument?.billingDocuments
      }

      billingDocuments.map((item) => {
        const currentDocument = item.originalDocument
        const amount = Number(item.billingAmount)
        const total = currentDocument.totalAmount
        const totalWhTax = currentDocument?.whTaxPrice || 0

        const proportion = amount / total
        const percentage = proportion * 100
        const roundedPercentage = Math.round(percentage * 100) / 100
        const whTax = Number((totalWhTax * (roundedPercentage / 100)).toFixed(2))
        const isMinusType = currentDocument.type === IncomeDocumentTypes.CREDIT
        const totalBeforeVat = currentDocument.incomeDocumentItems.reduce((a: any, b: any) => {
          return a + (b.pricePerUnit - b.discountPerUnit) * b.quantity
        }, 0)

        if (isMinusType) {
          sumInfo = {
            ...sumInfo,
            total: sumInfo.total - Number(item.billingAmount),
            whTaxTotal: sumInfo.whTaxTotal - whTax,
          }
        } else {
          sumInfo = {
            ...sumInfo,
            total: sumInfo.total + Number(item.billingAmount),
            whTaxTotal: sumInfo.whTaxTotal + whTax,
          }
        }

        let allocatedAmount = 0
        const sourceItems = currentDocument.incomeDocumentItems
        sourceItems.map((item: any, index: number) => {
          const netPricePerUnit = item.pricePerUnit - item.discountPerUnit
          // ให้รายการสุดท้ายรับเศษสตางค์จากการปัด เพื่อให้ฐานภาษีรวมตรงยอดวางบิล
          const subTotal = index === sourceItems.length - 1
            ? Number((amount - allocatedAmount).toFixed(2))
            : Number((amount * ((netPricePerUnit * item.quantity) / totalBeforeVat)).toFixed(2))
          allocatedAmount += subTotal
          const vatTotal =
            item.vat === 7
              ? Math.floor((subTotal - (subTotal * 100) / (100 + item.vat)) * 100) / 100
              : 0

          if (isMinusType) {
            sumInfo = {
              ...sumInfo,
              discountTotal: (sumInfo.discountTotal -= item.discountPerUnit * item.quantity),
              zeroVatItemPriceTotal: (sumInfo.zeroVatItemPriceTotal -=
                item.vat === 0 ? subTotal : 0),
              noVatItemPriceTotal: (sumInfo.noVatItemPriceTotal -= item.vat === 99 ? subTotal : 0),
              vatTotal: (sumInfo.vatTotal -= vatTotal),
              vatItemPriceTotal: (sumInfo.vatItemPriceTotal -=
                item.vat === 7 ? subTotal - vatTotal : 0),
            }
          } else {
            sumInfo = {
              ...sumInfo,
              discountTotal: (sumInfo.discountTotal += item.discountPerUnit * item.quantity),
              zeroVatItemPriceTotal: (sumInfo.zeroVatItemPriceTotal +=
                item.vat === 0 ? subTotal : 0),
              noVatItemPriceTotal: (sumInfo.noVatItemPriceTotal += item.vat === 99 ? subTotal : 0),
              vatTotal: (sumInfo.vatTotal += vatTotal),
              vatItemPriceTotal: (sumInfo.vatItemPriceTotal +=
                item.vat === 7 ? subTotal - vatTotal : 0),
            }
          }
        })
      })

      sumInfo = {
        ...sumInfo,
        subTotal: sumInfo.total - sumInfo.whTaxTotal,
      }
    }

    if (['debit', 'credit'].includes(document.type)) {
      document.linkingDocument?.incomeDocumentItems?.map((item: any) => {
        const netPrice = (item.pricePerUnit - item.discountPerUnit) * item.quantity

        if (document.vatType === 'included') {
          previousCorrectPrice += netPrice - item.vatPrice
        } else {
          previousCorrectPrice += netPrice
        }
      })

      sumInfo = {
        ...sumInfo,
        total: total - Number(productPriceInfo?.depositTotal),
        subTotal: total - sumInfo.whTaxTotal - Number(productPriceInfo?.depositTotal),
        depositTotal: productPriceInfo.depositTotal,
      }

      if (document.type === 'debit') {
        sumInfo = {
          ...sumInfo,
          correctPrice:
            previousCorrectPrice +
            sumInfo.vatItemPriceTotal +
            sumInfo.noVatItemPriceTotal +
            sumInfo.zeroVatItemPriceTotal,
          beforeCorrectPrice: previousCorrectPrice,
        }
      }

      if (document.type === 'credit') {
        sumInfo = {
          ...sumInfo,
          correctPrice:
            previousCorrectPrice -
            sumInfo.vatItemPriceTotal -
            sumInfo.noVatItemPriceTotal -
            sumInfo.zeroVatItemPriceTotal,
          beforeCorrectPrice: previousCorrectPrice,
        }
      }
    }

    // คำนวณยอดชำระจาก incomeDocumentPayments
    if (incomeDocumentPayments?.length) {
      const totalPaidAmount = incomeDocumentPayments.reduce((total: number, payment: any) => {
        return total + Number(payment.amount || 0)
      }, 0)

      let totalPaidWhTaxPrice =
        !incomeDocumentPayments?.length && (isCreditDebit || isExpenseOnly || isCombined)
          ? document?.whTaxPrice || 0
          : incomeDocumentPayments.reduce((total: number, payment: any) => {
              return total + Number(payment.whTaxPrice || 0)
            }, 0)
      // totalPaidWhTaxPrice += document.incomeDocumentItems.reduce((total: number, payment: any) => {
      //   return total + Number(payment.whTaxPrice || 0)
      // }, 0)

      // if (typeof totalItemsWhTax === 'number' && !!incomeDocumentPayments?.length) {
      //   totalPaidWhTaxPrice += totalItemsWhTax
      // }

      // console.log({sumInfo})
      // console.log({totalPaidWhTaxPrice})
      // console.log({totalPaidAmount})
      // console.log('sum', totalPaidWhTaxPrice + totalPaidAmount)
      // console.log('sum2', sumInfo.total - totalPaidAmount - totalPaidWhTaxPrice)

      // คำนวณ subTotal ใหม่ = ยอดรวมทั้งหมด - ยอดที่ชำระแล้ว
      sumInfo = {
        ...sumInfo,
        subTotal: sumInfo.total - totalPaidAmount - totalPaidWhTaxPrice,
        paidAmount: totalPaidAmount,
        paidWhTaxPrice: !!currentDepositDeductionDocuments.length
          ? document?.whTaxPrice || 0
          : totalPaidWhTaxPrice,
        remainingAmount: sumInfo.total - totalPaidAmount - totalPaidWhTaxPrice,
      }
    } else {
      sumInfo = {
        ...sumInfo,

        paidWhTaxPrice: !!currentDepositDeductionDocuments.length
          ? document?.whTaxPrice || 0
          : sumInfo?.paidWhTaxPrice || 0,
      }
    }

    // console.log(7777777)
    // console.log({sumInfo})

    const incomeDocumentPaymentDeductions = document?.incomeDocumentPaymentDeductions?.filter(
      (item) => !item?.isCombined && item?.isUseForPaid
    )
    // console.log({incomeDocumentPaymentDeductions})

    if (incomeDocumentPaymentDeductions?.length) {
      let paymentDeductionsDebitAmount = 0
      let paymentDeductionsDebitVatPrice = 0
      let paymentDeductionsDebitWhTaxPrice = 0
      let paymentDeductionsCreditAmount = 0
      let paymentDeductionsCreditVatPrice = 0
      let paymentDeductionsCreditWhTaxPrice = 0

      for (let i = 0; i < incomeDocumentPaymentDeductions.length; i++) {
        const currentPaymentDeductions = incomeDocumentPaymentDeductions[i]
        // console.log({currentPaymentDeductions})
        if (currentPaymentDeductions.type === 'expense-purchase-debit-note') {
          paymentDeductionsDebitAmount -= Number(currentPaymentDeductions?.amount || 0)
          paymentDeductionsDebitVatPrice += Number(
            currentPaymentDeductions?.currentDoc?.vatPrice || 0
          )
          paymentDeductionsDebitWhTaxPrice += Number(currentPaymentDeductions?.whTaxPrice || 0)
        } else {
          paymentDeductionsCreditAmount += Number(currentPaymentDeductions?.amount || 0)
          paymentDeductionsCreditVatPrice -= Number(
            currentPaymentDeductions?.currentDoc?.vatPrice || 0
          )
          paymentDeductionsCreditWhTaxPrice -= Number(currentPaymentDeductions?.whTaxPrice || 0)
        }
      }

      // console.log({paymentDeductionsAmount})
      // console.log('sumInfo?.paidAmount ==>>', sumInfo?.paidAmount)

      // คำนวณ subTotal ใหม่ = ยอดรวมทั้งหมด - ยอดที่ชำระแล้ว
      sumInfo = {
        ...sumInfo,
        subTotal: sumInfo.total - paymentDeductionsCreditAmount,
        // paidAmount:
        //   (sumInfo?.paidAmount || 0) +
        //   (Math.abs(paymentDeductionsCreditAmount) + Math.abs(paymentDeductionsCreditWhTaxPrice)),
        // paidWhTaxPrice:
        //   (sumInfo?.paidWhTaxPrice || 0) + Math.abs(paymentDeductionsCreditWhTaxPrice),
        remainingAmount: (sumInfo?.remainingAmount || 0) - Math.abs(paymentDeductionsCreditAmount),
        paymentDeductionsAmount: paymentDeductionsDebitAmount + paymentDeductionsCreditAmount,
        paymentDeductionsVatPrice: paymentDeductionsDebitVatPrice + paymentDeductionsCreditVatPrice,
        paymentDeductionsWhTaxPrice:
          paymentDeductionsDebitWhTaxPrice + paymentDeductionsCreditWhTaxPrice,
      }
    }

    if (document.billingHistories && document.billingHistories.length > 0) {
      let paymentCombinedAmount = 0
      let paymentCombinedWhTaxPrice = 0

      for (let i = 0; i < document.billingHistories.length; i++) {
        const currentBillingAmount = document.billingHistories[i]
        paymentCombinedAmount += Number(currentBillingAmount?.billingAmount || 0)
        paymentCombinedWhTaxPrice += Number(currentBillingAmount?.whTaxPrice || 0)
        // + Number(currentBillingAmount?.whTaxPrice || 0)
      }

      // console.log(5512525)
      // console.log({sumInfo})
      // console.log({paymentCombinedAmount})

      sumInfo = {
        ...sumInfo,
        subTotal: sumInfo.total - paymentCombinedAmount,
        remainingAmount:
          (!incomeDocumentPayments?.length ? sumInfo?.total || 0 : sumInfo?.remainingAmount || 0) -
          paymentCombinedAmount,
        paymentCombinedAmount,
        paidWhTaxPrice: (sumInfo?.paidWhTaxPrice || 0) + paymentCombinedWhTaxPrice,
      }
    }
    if (document.incomeDocumentPaymentFees && document.incomeDocumentPaymentFees.length > 0) {
      let paymentFeeAmount = 0

      for (let i = 0; i < document.incomeDocumentPaymentFees.length; i++) {
        const currentFeeAmount = document.incomeDocumentPaymentFees[i]
        const reason = Number(document.incomeDocumentPaymentFees[i].reason)
        paymentFeeAmount += Number(
          reason === -1 ? currentFeeAmount?.amount || 0 : -Math.abs(currentFeeAmount?.amount || 0)
        )
      }

      sumInfo = {
        ...sumInfo,
        subTotal: (sumInfo?.subTotal || 0) - paymentFeeAmount,
        remainingAmount: (sumInfo?.remainingAmount || 0) - paymentFeeAmount,
        paymentFeeAmount,
      }
    }
    // console.log({sumInfo})

    if (document.type === IncomeDocumentTypes.EXPENSE_COMBINED_PAYMENT_NOTE) {
      const currentSelectedItems = document?.childrenDocumentsJson || []
      const totalPaymentDepositBillAmount = currentSelectedItems.reduce((sum, item) => {
        const value = item?.billAmount ? Number(item.billAmount) : 0
        if (item.state === 'await-deduct') {
          return sum + value
        } else {
          return sum
        }
      }, 0)
      const totalCreditNoteBillAmount = currentSelectedItems.reduce((sum, item) => {
        const value = item?.billAmount ? Number(item.billAmount) : 0
        if (item.type === 'expense-purchase-credit-note') {
          return sum + value
        } else {
          return sum
        }
      }, 0)
      const totalPaymentDepositVatAmount = currentSelectedItems.reduce((sum, item) => {
        const value = item?.vatPrice ? ((Number(item?.billAmount || 0) * 100) / 107) * 0.07 : 0
        if (item.state === 'await-deduct') {
          return sum + value
        } else {
          return sum
        }
      }, 0)
      const totalCreditNoteVatAmount = currentSelectedItems.reduce((sum, item) => {
        const value = item?.vatPrice ? ((Number(item?.billAmount || 0) * 100) / 107) * 0.07 : 0
        if (item.type === 'expense-purchase-credit-note') {
          return sum + value
        } else {
          return sum
        }
      }, 0)
      const totalPaymentDepositWhTaxPriceAmount = currentSelectedItems.reduce((sum, item) => {
        const value = item?.whTaxPrice
          ? (item?.vatPrice ? (item?.billAmount * 100) / 107 : item?.billAmount) *
            (item?.whTaxPrice / (item?.totalAmount - item?.vatPrice))
          : 0
        if (item.state === 'await-deduct') {
          return sum + value
        } else {
          return sum
        }
      }, 0)
      const totalCreditNoteWhTaxPriceAmount = currentSelectedItems.reduce((sum, item) => {
        const value = item?.whTaxPrice
          ? (item?.vatPrice ? (item?.billAmount * 100) / 107 : item?.billAmount) *
            (item?.whTaxPrice / (item?.totalAmount - item?.vatPrice))
          : 0
        if (item.type === 'expense-purchase-credit-note') {
          return sum + value
        } else {
          return sum
        }
      }, 0)

      const totalBillAmount = currentSelectedItems.reduce((sum, item) => {
        const value = item?.billAmount ? Number(item.billAmount) : 0
        if (item.state === 'await-deduct' || item.type === 'expense-purchase-credit-note') {
          return sum - value
        } else {
          return sum + value
        }
      }, 0)
      let totalVatAmount = currentSelectedItems.reduce((sum, item) => {
        const value = item?.vatPrice ? ((Number(item?.billAmount || 0) * 100) / 107) * 0.07 : 0
        if (item.state === 'await-deduct' || item.type === 'expense-purchase-credit-note') {
          return sum + 0
        } else {
          return sum + value
        }
      }, 0)
      let totalWhTaxPriceAmount = currentSelectedItems.reduce((sum, item) => {
        const value = item?.whTaxPrice
          ? (item?.vatPrice ? (item?.billAmount * 100) / 107 : item?.billAmount) *
            (item?.whTaxPrice / (item?.totalAmount - item?.vatPrice))
          : 0
        if (item.state === 'await-deduct' || item.type === 'expense-purchase-credit-note') {
          return sum + 0
        } else {
          return sum + value
        }
      }, 0)

      const totalDecreaseBillAmount = Math.abs(
        totalCreditNoteBillAmount + totalPaymentDepositBillAmount
      )
      const totalDecreaseVatAmount = Math.abs(
        totalCreditNoteVatAmount + totalPaymentDepositVatAmount
      )
      const totalDecreaseWhTaxPriceAmount = Math.abs(
        totalCreditNoteWhTaxPriceAmount + totalPaymentDepositWhTaxPriceAmount
      )

      // totalBillAmount += -Math.abs(totalCreditNoteBillAmount + totalPaymentDepositBillAmount)
      totalVatAmount -= totalDecreaseVatAmount
      totalWhTaxPriceAmount -= totalDecreaseWhTaxPriceAmount

      sumInfo = {
        ...sumInfo,
        comBinedData: {
          totalPaymentDepositBillAmount,
          totalCreditNoteBillAmount,
          totalPaymentDepositVatAmount,
          totalCreditNoteVatAmount,
          totalPaymentDepositWhTaxPriceAmount,
          totalCreditNoteWhTaxPriceAmount,
          totalBillAmount,
          totalVatAmount,
          totalWhTaxPriceAmount,
          totalDecreaseWhTaxPriceAmount,
          totalDecreaseBillAmount,
          totalDecreaseVatAmount,
        },
      }
    }

    setProductPriceInfo(sumInfo)
  }

  const renderTableCombinedItem = () => {
    return (
      <Table>
        <thead>
          <tr>
            <th className={'py-1 fw-bold'}>{'ลำดับที่'}</th>
            <th className={'py-1 fw-bold'}>{'เลขที่เอกสาร'}</th>
            <th className={'py-1 fw-bold'}>{'วันที่ออก'}</th>
            <th className={'py-1 fw-bold'}>{'วันที่ครบกำหนด'}</th>
            <th className={'py-1 fw-bold text-end'}>{'มูลค่าสุทธิ'}</th>
            <th className={'py-1 fw-bold text-end'}>{'จำนวนเงินวางบิล'}</th>
            <th className={'py-1 fw-bold text-end'}>{'หัก ณ ที่จ่าย'}</th>
          </tr>
        </thead>
        <tbody>
          {document?.childrenDocumentsJson?.map((item: any, index: number) => {
            return (
              <tr>
                <td className={'py-1'}>{`${index + 1}.`}</td>
                <td className={'py-1'}>
                  <Link
                    to={`${getRevenuePrefixPath(item?.type)}/${item?.id}/detail`}
                    className={'fw-bold'}
                  >
                    {item?.code}
                  </Link>
                </td>
                <td className={'py-1'}>
                  {item?.createdAt ? moment(item?.createdAt).format('D/M/Y') : '-'}
                </td>
                <td className={'py-1'}>
                  {item?.createdAt ? moment(item?.expiryDate).format('D/M/Y') : '-'}
                </td>{' '}
                <td className={'py-1 text-end'}>{numberFormat(item?.totalAmount || 0)}</td>
                <td className={'py-1 text-end'}>{numberFormat(item?.billAmount || 0)}</td>
                <td className={'py-1 text-end	'}>
                  {numberFormat(
                    item?.whTaxPrice
                      ? (item?.vatPrice ? (item?.billAmount * 100) / 107 : item?.billAmount) *
                          (item?.whTaxPrice / (item?.totalAmount - item?.vatPrice))
                      : 0
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </Table>
    )
  }

  const renderTableProductItem = () => {
    return (
      <Table>
        <thead>
          <tr>
            <th className={'py-1 fw-bold'}>{'ลำดับที่'}</th>
            {isInExpensePage && (
              <th className={'py-1 fw-bold'}>
                {isInExpenseRecordPage || isInExpenseDepositPayment || isAP
                  ? 'เลขบัญชี'
                  : 'รหัสสินค้า'}
              </th>
            )}
            <th className={'py-1 fw-bold'}>
              {isInExpenseRecordPage || isInExpenseDepositPayment || isAP
                ? ' บัญชี/ค่าใช้จ่าย'
                : 'สินค้า/บริการ'}
            </th>
            <th className={'py-1 fw-bold text-end'}>{'จำนวน'}</th>
            {!isInExpenseRecordPage && !isInExpenseDepositPayment && !isAP && (
              <>{isInExpensePage && <th className={'py-1 fw-bold'}>{'หน่วย'}</th>}</>
            )}
            {!isInExpenseRequestPurchaseOrder && (
              <>
                <th className={'py-1 fw-bold text-end'}>{'ราคา/หน่วย'}</th>
                <th className={'py-1 fw-bold text-end'}>{'ส่วนลด/หน่วย'}</th>
                <th className={'py-1 fw-bold'}>{'ภาษี'}</th>
                <th className={'py-1 fw-bold text-end'}>{'มูลค่าก่อนภาษี'}</th>
                <th className={'py-1 fw-bold text-end'}>{'หัก ณ ที่จ่าย'}</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {document?.incomeDocumentItems?.map((item: any, index: number) => {
            // ใช้ inventoryProduct ถ้ามี หรือ fallback ไปใช้ treatmentItem
            const product = item.inventoryProduct || item.treatmentItem
            // REQ-002: รายการค่าธรรมเนียม/ปรับปรุง ไม่ผูกสินค้า จึงแสดงข้อมูลบัญชีแทน
            const isAccountOnlyItem =
              !item.inventoryProductId && !item.treatmentItemId && !!item.accountCodeId
            const showAccountInfo =
              isInExpenseRecordPage || isInExpenseDepositPayment || isAP || isAccountOnlyItem
            const productCode = item.inventoryProduct 
              ? item.inventoryProduct.code 
              : item?.treatmentItem?.barcode || item?.treatmentItem?.code
            const productName = item.inventoryProduct
              ? item.inventoryProduct.nameTh
              : item.treatmentItem?.nameTh
            const productUnit = item.inventoryProduct
              ? item.inventoryProduct.unit
              : item.treatmentItem?.unit

            return (
              <tr>
                <td className={'py-1'}>{`${index + 1}.`}</td>
                {isInExpensePage && (
                  <td className={'py-1'}>
                    {showAccountInfo ? item.accountCodeItem?.code : productCode}
                  </td>
                )}
                <td className={'py-1'}>
                  {showAccountInfo ? item.accountCodeItem?.name : productName}
                </td>
                <td className={'py-1 text-end'}>{item.quantity}</td>
                {!isInExpenseRecordPage && !isInExpenseDepositPayment && !isAP && (
                  <>
                    {isInExpensePage && (
                      <th className={'py-1'}>{productUnit || '-'}</th>
                    )}
                  </>
                )}
                {!isInExpenseRequestPurchaseOrder && (
                  <>
                    <td className={'py-1 text-end'}>{numberFormat(item.pricePerUnit)}</td>
                    <td className={'py-1 text-end'}>{numberFormat(item.discountPerUnit)}</td>
                    <td className={'py-1'}>{item.vat === 99 ? 'ไม่มี' : `${item.vat}%`}</td>
                    <td className={'py-1 text-end'}>{numberFormat(item.beforeVatPrice)}</td>
                    <td className={'py-1 text-end	'}>{numberFormat(item.whTaxPrice)}</td>
                  </>
                )}
              </tr>
            )
          })}
        </tbody>
      </Table>
    )
  }

  const renderTableBillingDocument = () => {
    let billingDocuments = document?.billingDocuments

    if (isReceiptBilling) {
      billingDocuments = document.linkingDocument?.billingDocuments
    }

    return (
      <Table>
        <thead>
          <tr>
            <th className={'py-1 fw-bold'}>{'ลำดับที่'}</th>
            <th className={'py-1 fw-bold'}>{'เลขที่เอกสาร'}</th>
            <th className={'py-1 fw-bold'}>{'วันที่ออก'}</th>
            <th className={'py-1 fw-bold'}>{'วันที่ครบกำหนด'}</th>
            <th className={'py-1 fw-bold'}>{'มูลค่าสุทธิ'}</th>
            <th className={'py-1 fw-bold'}>{'จำนวนเงินวางบิล'}</th>
            <th className={'py-1 fw-bold'}>{'หัก ณ ที่จ่าย'}</th>
          </tr>
        </thead>
        <tbody>
          {billingDocuments?.map((item: any, index: number) => {
            const original = item.originalDocument
            return (
              <tr>
                <td>{`${index + 1}.`}</td>
                <td>
                  <Link
                    to={`${getRevenuePrefixPath(original.type)}/${original.id}/detail`}
                    className={'fw-bold'}
                    target={'_blank'}
                  >
                    {original.code}
                  </Link>
                </td>
                <td>{original.date}</td>
                <td>{original.expiryDate}</td>
                <td>{numberFormat(original.totalAmount)}</td>
                <td>{numberFormat(item.billingAmount)}</td>
                <td>{numberFormat(item.whTaxPrice)}</td>
              </tr>
            )
          })}
        </tbody>
      </Table>
    )
  }

  const renderTablePaymentDocuments = () => {
    return (
      <Table>
        <thead>
          <tr>
            <th className={'py-1 fw-bold'}>{'ลำดับที่'}</th>
            <th className={'py-1 fw-bold'}>{'วันที่ชำระ'}</th>
            <th className={'py-1 fw-bold'}>{'ธนาคาร'}</th>
            <th className={'py-1 fw-bold'}>{'เลขที่บัญชี'}</th>
            <th className={'py-1 fw-bold'}>{'ชื่อบัญชี'}</th>
            <th className={'py-1 fw-bold text-end'}>{'จำนวนเงิน'}</th>
            <th className={'py-1 fw-bold text-end'}>{'หัก ณ ที่จ่าย'}</th>
            <th className={'py-1 fw-bold'}>{'เลขที่เช็ค/โอน'}</th>
            <th className={'py-1 fw-bold'}>{'หมายเหตุ'}</th>
          </tr>
        </thead>
        <tbody>
          {incomeDocumentPayments?.map((payment: any, index: number) => {
            return (
              <tr key={payment.id}>
                <td className={'py-1'}>{`${index + 1}.`}</td>
                <td className={'py-1'}>{payment.date}</td>
                <td className={'py-1'}>{payment.bank || '-'}</td>
                <td className={'py-1'}>{payment.accountNo || '-'}</td>
                <td className={'py-1'}>{payment.accountName || '-'}</td>
                <td className={'py-1 text-end'}>{numberFormat(payment.amount)}</td>
                <td className={'py-1 text-end'}>
                  {numberFormat(
                    (payment.whTaxPrice !== 0
                      ? payment.whTaxPrice
                      : payment.extraWithholdingTaxItems?.reduce(
                          (a: any, b: any) => a + b.whTaxPrice,
                          0
                        )) || 0
                  )}
                </td>
                <td className={'py-1'}>{payment.chequeNumber || '-'}</td>
                <td className={'py-1'}>{payment.note || '-'}</td>
              </tr>
            )
          })}
        </tbody>
      </Table>
    )
  }

  useEffect(() => {
    if (!document) return
    calculateSummaryPriceInfo()
  }, [document])

  return (
    <KTCard>
      <KTCardBody className={'py-3 px-4'}>
        <Row>
          <Col xs={12}>
            <h4>{isInExpensePage ? 'ข้อมูลผู้ขาย' : 'ข้อมูลลูกค้า'}</h4>
          </Col>
          <Col md={4}>
            <strong>{isInExpensePage ? 'ชื่อผู้ขาย' : 'ชื่อลูกค้า'}</strong>
            <p className={'mb-0'}>{document?.labCompany?.name || '-'}</p>
            <p className={'mb-0'} style={{marginTop: -4}}>{`โทร : ${
              document?.labCompany?.tel || '-'
            }`}</p>
          </Col>
          <Col md={3}>
            <strong>{'ที่อยู่'}</strong>
            <p className={'mb-1'}>{document?.labCompany?.fullAddress || '-'}</p>
          </Col>
          <Col>
            <strong>{'วันที่ออก'}</strong>
            <p className={'mb-1'}>{document.date}</p>
          </Col>
          <Col>
            {document.type === IncomeDocumentTypes.EXPENSE_REQUEST_PURCHASE_ORDER ||
            document.type === IncomeDocumentTypes.EXPENSE_PURCHASE_ORDER ? (
              <strong>{'วันที่กำหนดส่ง'}</strong>
            ) : (
              <strong>{'วันที่ใช้ได้ถึง'}</strong>
            )}
            <p className={'mb-1'}>{document.expiryDate}</p>
          </Col>
          {document?.conditionDay && (
            <Col>
              <strong>{'เงื่อนไขการชำระเงิน'}</strong>
              <p className={'mb-1'}>{`${document?.conditionDay} วัน`}</p>
            </Col>
          )}
        </Row>
        {isInExpenseRequestPurchaseOrder && (
          <>
            <hr className={'my-2'} />
            <Row>
              <Col xs={12}>
                <h4>{'รายการใบสั่งซื้อ'}</h4>
              </Col>
              <Col>
                <Table>
                  <thead>
                    <tr>
                      <th className={'py-1 fw-bold'}>ลำดับที่</th>
                      <th className={'py-1 fw-bold'}>รหัสเอกสาร</th>
                      <th className={'py-1 fw-bold'}>จำนวนรายการ</th>
                      <th className={'py-1 fw-bold'}>สถานะ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {document.childrenDocuments.map((doc, index) => {
                      return (
                        <tr key={`${doc.id}-${index}`}>
                          <td className={'py-1'}>{`${index + 1}.`}</td>
                          <td className={'py-1'}>
                            <Link
                              to={`/expense-purchase-order/${doc.id}/detail`}
                              className={'fw-bold'}
                              target={'_blank'}
                            >
                              {doc.code}
                            </Link>
                          </td>
                          <td className={'py-1'}>{`${doc.incomeDocumentItems.length} รายการ`}</td>
                          <td className={'py-1'}>
                            <StateLabel state={doc.state} />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              </Col>
            </Row>
          </>
        )}
        {!isInExpenseRequestPurchaseOrder && (
          <>
            <hr className={'my-2'} />
            <Row>
              <Col xs={12}>
                <h4>{'ข้อมูลราคาและภาษี'}</h4>
              </Col>
              <Col>
                <strong>{'ประเภทราคา'}</strong>
                <p className={'mb-1'}>
                  {document.vatType === 'included'
                    ? 'รวมภาษี'
                    : document.vatType === 'excluded'
                    ? 'แยกภาษี'
                    : 'ไม่มี'}
                </p>
              </Col>
              {shouldRenderIssueTaxInvoice && (
                <Col>
                  <strong>{'ประเภทใบกำกับภาษี'}</strong>
                  <p className={'mb-1'}>
                    {!!document.issueTaxInvoice ? 'เป็นใบกำกับภาษี' : 'ไม่ใช่ใบกำกับภาษี'}
                  </p>
                </Col>
              )}
            </Row>
          </>
        )}
        {document?.incomeDocumentDebt && (
          <>
            <hr className={'my-2'} />
            <Row>
              <Col xs={12}>
                <h4>
                  {document.type === IncomeDocumentTypes.DEBIT
                    ? 'ข้อมูลการเพิ่มหนี้'
                    : 'ข้อมูลการลดหนี้'}
                </h4>
              </Col>
              <Col xs={6}>
                <strong>
                  {document.type === IncomeDocumentTypes.DEBIT
                    ? 'สาเหตุการเพิ่มหนี้'
                    : 'ข้อมูลการลดหนี้'}
                </strong>
              </Col>
              <Col xs={6}>
                <strong>{'คำอธิบายอย่างย่อ'}</strong>
                <p>{document?.incomeDocumentDebt?.shortDescription}</p>
              </Col>
              <Col xs={6}>
                <strong>
                  {document.type === IncomeDocumentTypes.DEBIT
                    ? 'มีการส่งสินค้าเพิ่มหรือไม่'
                    : 'ข้อมูลการลดหนี้'}
                </strong>
              </Col>
            </Row>
          </>
        )}
        <hr className={'my-2'} />
        <Row>
          <Col xs={12} className={'d-flex'}>
            <h4>{'รายการ'}</h4>
          </Col>
          <Col>
            {document.type === 'billing' || isReceiptBilling
              ? renderTableBillingDocument()
              : document.type === 'expense-combined-payment-note'
              ? renderTableCombinedItem()
              : renderTableProductItem()}
          </Col>
        </Row>
        {depositDocuments?.length > 0 && (
          <>
            <hr className={'my-2'} />
            <Row>
              <Col xs={12}>
                <h4>{'เงินมัดจำ'}</h4>
              </Col>
              <Col>
                <AccountOrderDepositSummary
                  depositDocuments={depositDocuments}
                  priceSummaryInfo={depositPriceInfo}
                />
              </Col>
            </Row>
          </>
        )}
        {/* {incomeDocumentPayments?.length > 0 && (
          <>
            <hr className={'my-2'} />
            <Row>
              <Col xs={12}>
                <h4>{'ข้อมูลการชำระเงิน'}</h4>
              </Col>
              <Col>{renderTablePaymentDocuments()}</Col>
            </Row>
          </>
        )} */}
        {!!(document as any)?.debtReason && (
          <>
            <hr className={'my-2'} />
            <Row>
              <Col xs={12}>
                <h4>{'ข้อมูลการลดหนี้'}</h4>
              </Col>
              <Col>
                <div className='container'>
                  <div className='card'>
                    <div className='card-body p-0 mt-3'>
                      <div className='row mb-3'>
                        <div className='col-12 col-md-6 fw-bold'>
                          {document?.type === IncomeDocumentTypes.EXPENSE_PURCHASE_CREDIT_NOTE
                            ? 'สาเหตุของการลดหนี้'
                            : 'สาเหตุการเพิ่มหนี้'}
                        </div>
                        <div className='col-12 col-md-6'>
                          <span className='text-muted'>
                            {(() => {
                              const debt = (
                                document?.type === IncomeDocumentTypes.EXPENSE_PURCHASE_CREDIT_NOTE
                                  ? DEBT_REDUCTION_REASONS
                                  : DEBT_ADDITION_REASONS
                              ) as {value: string; label: string}[]

                              const found = debt.find(
                                (item) => item.value === (document as any)?.debtReason
                              )

                              return found ? found.label : (document as any)?.debtReason || '-'
                            })()}
                          </span>
                        </div>
                      </div>

                      <div className='row mb-3'>
                        <div className='col-12 col-md-6 fw-bold'>คำอธิบายอย่างย่อ</div>
                        <div className='col-12 col-md-6'>
                          <span className='text-muted'>
                            {(document as any)?.shortDescription || '-'}
                          </span>
                        </div>
                      </div>

                      <div className='row mb-3'>
                        <div className='col-12 col-md-6 fw-bold'>
                          {document?.type === IncomeDocumentTypes.EXPENSE_PURCHASE_CREDIT_NOTE
                            ? 'มีการคืนสินค้าเพิ่มหรือไม่'
                            : 'มีการรับสินค้าเพิ่มหรือไม่'}
                        </div>
                        <div className='col-12 col-md-6'>
                          <span className='text-muted'>
                            {(() => {
                              const options = (
                                document?.type === IncomeDocumentTypes.EXPENSE_PURCHASE_CREDIT_NOTE
                                  ? PRODUCT_RETURN_OPTIONS
                                  : PRODUCT_RECEIVE_OPTIONS
                              ) as {value: string; label: string}[]

                              const found = options.find((item) =>
                                document?.type === IncomeDocumentTypes.EXPENSE_PURCHASE_CREDIT_NOTE
                                  ? item.value === (document as any)?.hasProductReturn
                                  : item.value === (document as any)?.hasProductReceive
                              )

                              return found
                                ? found.label
                                : (document?.type ===
                                  IncomeDocumentTypes.EXPENSE_PURCHASE_CREDIT_NOTE
                                    ? (document as any)?.hasProductReturn
                                    : (document as any)?.hasProductReceive) || '-'
                            })()}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </>
        )}
        {!isInExpenseRequestPurchaseOrder && (
          <>
            <hr className={'my-2'} />
            <Row>
              <Col xs={12}>
                <h4>{'เอกสารที่ออก'}</h4>
              </Col>
              {isInExpensePage && (
                <Col>
                  <ExpenseAccountOrderSummary
                    isInCreditDebit={isCreditDebit}
                    document={document}
                    previousItems={
                      !!(document as any)?.debtReason
                        ? document?.linkingDocument?.incomeDocumentItems
                        : undefined
                    }
                    vatType={document.vatType}
                    productPriceInfo={productPriceInfo}
                    docType={document.type}
                    linkingDocument={document?.linkingDocument}
                    paymentsLength={incomeDocumentPayments?.length}
                  />
                </Col>
              )}
              {!isInExpensePage && (
                <Col>
                  {isExpense ? (
                    <ExpenseAccountOrderSummary
                      isInCreditDebit={isCreditDebit}
                      document={document}
                      vatType={document.vatType}
                      productPriceInfo={productPriceInfo}
                      linkingDocument={document?.linkingDocument}
                      paymentsLength={incomeDocumentPayments?.length}
                    />
                  ) : (
                    <AccountOrderSummary
                      vatType={document.vatType}
                      productPriceInfo={productPriceInfo}
                    />
                  )}
                </Col>
              )}
            </Row>
          </>
        )}
        {document?.note && (
          <>
            <hr className={'my-2'} />
            <Row>
              <Col xs={12}>
                <h4>{'หมายเหตุ'}</h4>
              </Col>
              <Col>
                <p>{`- ${document.note}`}</p>
              </Col>
            </Row>
          </>
        )}
      </KTCardBody>
    </KTCard>
  )
}

export default TabDocumentDetail
